import { zodResolver } from "@hookform/resolvers/zod";
import type { ActionFunctionArgs, MetaFunction } from "@remix-run/node";
import { type LoaderFunctionArgs } from "@remix-run/node";
import { redirect, useLoaderData, useSubmit } from "@remix-run/react";
import { createBrowserClient } from "@supabase/ssr";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import { IS_MOCK_AUTH_SERVER } from "~/infra/auth/mock-auth";
import { getServerAuthUser } from "~/infra/auth/server";
import { getAuthAdapter } from "~/resolvers/auth_resolver";
import { FirebaseAuthAdapter } from "~/core/adapters/auth/firebase-auth-adapter";
import { BackendAuthRepository } from "~/core/adapters/backend/backend-auth-repository";

export const meta: MetaFunction = () => {
  return [
    { title: "許認可OCR" },
    { name: "description", content: "許認可OCR App" },
  ];
};
const formSchema = z.object({
  email: z.string().min(1, "メールアドレスを入力してください"),
  password: z.string().min(1, "パスワードを入力してください"),
});

export async function loader({ request }: LoaderFunctionArgs) {
  if (IS_MOCK_AUTH_SERVER) {
    return redirect("/documents/new");
  }

  const user = await getServerAuthUser(request);
  if (user) {
    return redirect("/documents/new");
  }

  // 認証プロバイダーに応じた環境変数を返す
  const provider = process.env.VITE_AUTH_PROVIDER || "supabase";
  const env: Record<string, string> = {};

  if (provider === "firebase") {
    env.FIREBASE_API_KEY = process.env.VITE_FIREBASE_API_KEY!;
    env.FIREBASE_AUTH_DOMAIN = process.env.VITE_FIREBASE_AUTH_DOMAIN!;
    env.FIREBASE_PROJECT_ID = process.env.VITE_FIREBASE_PROJECT_ID!;
    env.FIREBASE_STORAGE_BUCKET = process.env.VITE_FIREBASE_STORAGE_BUCKET!;
    env.FIREBASE_MESSAGING_SENDER_ID =
      process.env.VITE_FIREBASE_MESSAGING_SENDER_ID!;
    env.FIREBASE_APP_ID = process.env.VITE_FIREBASE_APP_ID!;
  } else {
    env.SUPABASE_URL = process.env.SUPABASE_URL!;
    env.SUPABASE_ANON_KEY = process.env.SUPABASE_ANON_KEY!;
  }

  return { env, provider };
}

export async function action({ request }: ActionFunctionArgs) {
  return redirect("/documents/new");
}

export default function Index() {
  const { env, provider } = useLoaderData<typeof loader>();
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const submit = useSubmit();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const authRepository = useMemo(() => new BackendAuthRepository(), []);

  const onSubmit = async (formData: z.infer<typeof formSchema>) => {
    setIsSubmitting(true);

    try {
      if (provider === "firebase") {
        const authAdapter = getAuthAdapter();
        if (authAdapter instanceof FirebaseAuthAdapter) {
          console.log("[LOGIN] Starting signInWithEmailAndPassword", {
            email: formData.email,
          });
          const result = await authAdapter.signInWithEmailAndPassword(
            formData.email,
            formData.password
          );
          const { user, error } = result;

          if (error) {
            console.log("[LOGIN] signInWithEmailAndPassword error", {
              code: (error as any).code,
            });
            if ((error as any).code === "auth/multi-factor-auth-required") {
              form.setError("password", {
                message:
                  "このアカウントは多要素認証が必要です。管理者にお問い合わせください。",
              });
              setIsSubmitting(false);
              return;
            }

            form.setError("password", {
              message: "メールアドレスまたはパスワードが正しくありません",
            });
            setIsSubmitting(false);
          } else if (user) {
            console.log("[LOGIN] signInWithEmailAndPassword success", {
              userId: user.id,
              email: user.email,
            });
            try {
              const token = await authAdapter.getAccessToken();
              if (token) {
                await authRepository.resetFailedLoginAttempts(token);
              }
            } catch (apiError) {
              console.error("[LOGIN] API call failed", apiError);
            }

            submit(formData, {
              method: "post",
            });
          }
        }
      } else {
        const supabase = createBrowserClient(
          env.SUPABASE_URL,
          env.SUPABASE_ANON_KEY
        );
        const { data, error } = await supabase.auth.signInWithPassword({
          email: formData.email,
          password: formData.password,
        });

        if (error) {
          form.setError("password", {
            message: error.message,
          });
          setIsSubmitting(false);
        } else if (data?.session) {
          try {
            const token = data.session.access_token;
            if (token) {
              await authRepository.resetFailedLoginAttempts(token);
            }
          } catch (apiError) {
            console.error("[LOGIN] API call failed", apiError);
          }

          submit(formData, {
            method: "post",
          });
        }
      }
    } catch (error) {
      form.setError("password", {
        message: "ログイン処理中にエラーが発生しました",
      });
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex items-center justify-center w-full h-screen px-4">
      <Card className="max-w-sm mx-auto">
        <CardHeader>
          <CardTitle className="text-2xl">ログイン</CardTitle>
          <CardDescription>
            メールアドレスとパスワードを入力してください
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>メールアドレス</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>パスワード</FormLabel>
                    <FormControl>
                      <Input type="password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "ログイン中..." : "ログイン"}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
